import React, { useRef, useEffect, memo } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// SourcePreviewCard — BEFORE-state webcam/file preview (Phase 1)
//
// 358×201px card shown in the desktop BEFORE layout (no result pills).
// Webcam mode: live video feed with scanlines, corner brackets, LIVE dot.
// File mode: thumbnail or "Select a file" prompt.
// ─────────────────────────────────────────────────────────────────────────────

interface VideoSource {
  kind: 'file' | 'stream';
  name: string;
  thumbUrl?: string;
}

interface SourcePreviewCardProps {
  /** null = no source chosen yet (first use) — renders a neutral "select a source" card */
  sourceMode: 'webcam' | 'file' | null;
  videoSource: VideoSource | null;
  localStream?: MediaStream | null;
  frozenFrameUrl?: string | null;
  isFileStream?: boolean;
}

const CARD_W = 358;
const CARD_H = 201;

const SourcePreviewCard = memo(React.forwardRef<HTMLDivElement, SourcePreviewCardProps>(
  function SourcePreviewCard({ sourceMode, videoSource, localStream, frozenFrameUrl, isFileStream }, ref) {
    const videoRef = useRef<HTMLVideoElement | null>(null);

    useEffect(() => {
      const el = videoRef.current;
      if (!el) return;
      if (localStream) {
        el.srcObject = localStream;
        el.muted = true;
        el.play().catch(() => {});
      } else {
        el.srcObject = null;
      }
    }, [localStream]);

    // ── Shared card container style ──
    const cardStyle: React.CSSProperties = {
      width: CARD_W,
      height: CARD_H,
      flexShrink: 0,
      background: '#000',
      borderRadius: 14,
      border: '1px solid rgba(0,0,0,0.14)',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
    };

    // ── Corner brackets ──
    const cornerBrackets = (
      <>
        {/* Top-left */}
        <div style={{ position: 'absolute', top: 5, left: 5, width: 10, height: 10, borderColor: 'rgba(27,94,255,0.32)', borderStyle: 'solid', borderWidth: '1.5px 0 0 1.5px', zIndex: 3 }} />
        {/* Top-right */}
        <div style={{ position: 'absolute', top: 5, right: 5, width: 10, height: 10, borderColor: 'rgba(27,94,255,0.32)', borderStyle: 'solid', borderWidth: '1.5px 1.5px 0 0', zIndex: 3 }} />
        {/* Bottom-left */}
        <div style={{ position: 'absolute', bottom: 5, left: 5, width: 10, height: 10, borderColor: 'rgba(27,94,255,0.32)', borderStyle: 'solid', borderWidth: '0 0 1.5px 1.5px', zIndex: 3 }} />
        {/* Bottom-right */}
        <div style={{ position: 'absolute', bottom: 5, right: 5, width: 10, height: 10, borderColor: 'rgba(27,94,255,0.32)', borderStyle: 'solid', borderWidth: '0 1.5px 1.5px 0', zIndex: 3 }} />
      </>
    );

    // ── No source selected yet (first use) ──
    // Neutral state: no pulsing blue ring, no WEBCAM label — nothing that
    // implies a source is already active. The input bar tooltip guides the
    // user to pick Webcam or File.
    if (sourceMode === null) {
      return (
        <div ref={ref} data-testid="source-preview-empty" style={cardStyle}>
          {cornerBrackets}
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth="1.5" style={{ zIndex: 1 }}>
            <path d="M12 5v14M5 12h14" strokeLinecap="round" />
          </svg>
          <div style={{
            fontSize: 9, color: 'rgba(255,255,255,0.25)',
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: '0.06em', zIndex: 1,
          }}>
            SELECT A SOURCE
          </div>
        </div>
      );
    }

    // ── Webcam mode ──
    if (sourceMode === 'webcam') {
      return (
        <div ref={ref} style={cardStyle}>
          <style>{`
            @keyframes spc-ring-pulse {
              0%, 100% { border-color: rgba(27,94,255,0.22); }
              50% { border-color: rgba(27,94,255,0.7); }
            }
          `}</style>
          {cornerBrackets}

          {/* Live video feed */}
          {localStream ? (
            <video
              ref={videoRef}
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover',
                transform: isFileStream ? 'none' : 'scaleX(-1)',
                zIndex: 1,
              }}
              muted
              playsInline
              autoPlay
              {...({ 'webkit-playsinline': 'true' } as React.HTMLAttributes<HTMLVideoElement>)}
            />
          ) : frozenFrameUrl ? (
            <img
              src={frozenFrameUrl}
              alt="Last frame"
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover', zIndex: 1, opacity: 0.8,
              }}
            />
          ) : (
            /* Awaiting camera — pulsing ring */
            <>
              <div style={{
                width: 38, height: 38, borderRadius: '50%',
                border: '1.5px solid rgba(27,94,255,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'spc-ring-pulse 2.2s ease-in-out infinite',
                zIndex: 1,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.42)" strokeWidth="1.5">
                  <path d="M23 7l-7 5 7 5V7z" />
                  <rect x="1" y="5" width="15" height="14" rx="2" />
                </svg>
              </div>
              <div style={{
                fontSize: 9, color: 'rgba(255,255,255,0.2)',
                fontFamily: "'JetBrains Mono', monospace",
                letterSpacing: '0.06em', zIndex: 1,
              }}>
                WEBCAM
              </div>
            </>
          )}
        </div>
      );
    }

    // ── File mode ──
    // Portrait videos should show full height with transparent sides (no black bands).
    // Use objectFit:contain on a transparent background so the page colour shows through.
    const thumbUrl = videoSource?.thumbUrl ?? null;
    const fileName = videoSource?.name ?? null;

    return (
      <div ref={ref} style={{ ...cardStyle, background: 'transparent', border: '1px solid rgba(0,0,0,0.10)', borderRadius: 14 }}>
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt={fileName ?? 'File preview'}
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'contain', zIndex: 1, opacity: 0.85,
            }}
          />
        ) : (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, zIndex: 1,
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(0,0,0,0.25)" strokeWidth="1.5">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <div style={{
              fontSize: 11, color: 'rgba(0,0,0,0.3)',
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: '0.03em',
            }}>
              {fileName ?? 'Select a file'}
            </div>
          </div>
        )}
        {fileName && (
          <div style={{
            position: 'absolute', bottom: 8, left: 8, right: 8,
            fontSize: 9, color: 'rgba(0,0,0,0.35)',
            fontFamily: "'JetBrains Mono', monospace",
            letterSpacing: '0.03em',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
            zIndex: 2,
            background: 'rgba(255,255,255,0.6)',
            padding: '2px 6px', borderRadius: 4,
          }}>
            {fileName}
          </div>
        )}
      </div>
    );
  }
));

SourcePreviewCard.displayName = 'SourcePreviewCard';
export default SourcePreviewCard;
